import { Component, OnInit } from '@angular/core';
import {
  BreedDto,
  ClienteUnicoAppDto,
  ClienteUnicoServiceProxy,
  CreateUpdatePetDto,
  PetDto,
  SpeciesDto,
  VetServiceProxy,
} from '../../services/service-proxy/service-proxies';
import { AppService } from 'src/app/services/app.service';
import { finalize, firstValueFrom } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { VetExtendedServiceProxy } from 'src/app/services/service-proxy/service-proxies-vet';

@Component({
  selector: 'app-pet-form',
  templateUrl: './pet-form.component.html',
  styleUrls: ['./pet-form.component.scss'],
})
export class PetFormComponent implements OnInit {

  urlPet = '/assets/images/camera.png';

  isEdit = false;
  isLoading = false;

  idUnico;
  cliente?: ClienteUnicoAppDto;

  pet_id: string | null | undefined;
  pet: CreateUpdatePetDto;

  // Sexo: fijo Hembra/Macho, mapeado a los ids de genero del backend (1=Male,
  // 2=Female). El prototipo los muestra como botones en ese orden.
  generos = [
    { id: 2, etiqueta: 'Hembra' },
    { id: 1, etiqueta: 'Macho' },
  ];

  especies: SpeciesDto[] = [];
  razas: BreedDto[] = [];

  today = new Date();

  imgSelectedFile?: File;
  imgSelectedURLData?: string;

  // Fecha de nacimiento: exacta, o aproximada (edad) cuando no se sabe la exacta.
  fechaNacimiento?: string;
  fechaAproximada = false;
  edadValor?: number;
  edadUnidad: 'anios' | 'meses' = 'anios';

  // Buscador de raza (autocompletar sobre el catalogo de la especie elegida).
  razaTexto = '';
  razaListaAbierta = false;

  constructor(private route: ActivatedRoute,
              private snackBar: MatSnackBar,
              private clienteUnicoService: ClienteUnicoServiceProxy,
              private vetService: VetServiceProxy,
              private vetExtendedService: VetExtendedServiceProxy,
              private router: Router,
              private appService: AppService) {

    this.idUnico = appService.idUnico!;
    this.pet = this.createPet();

    this.route.paramMap.subscribe((params) => {
      this.pet_id = params.get('pet_id')!;
      if (this.pet_id) this.isEdit = true;
    });
  }

  ngOnInit(): void {

    const data = this.appService.getClienteAppData(this.idUnico);

    if (data) {
      this.cliente = data;
      this.initData(this.pet_id!);
    } else {
      this.isLoading = true;
      this.clienteUnicoService
        .getForApp(this.idUnico, null)
        .pipe(finalize(() => (this.isLoading = false)))
        .subscribe((response) => {
          this.appService.saveClienteAppData(response);
          this.cliente = response;
          this.initData(this.pet_id!);
        });
    }

    this.vetService.getSpecies().subscribe((especies) => {
      // "Desconocido" es una especie placeholder del catalogo, sin razas: no
      // sirve para dar de alta y ademas dejaba el buscador de raza bloqueado.
      this.especies = (especies ?? []).filter((s) => (s.name ?? '').toUpperCase() !== 'DESCONOCIDO');
      // En edicion, ya hay especie: cargo sus razas.
      if (this.isEdit && this.pet.speciesId) this.reloadBreed(this.pet.speciesId, true);
    });
  }

  initData(petId: string) {

    if (!this.isEdit) return;

    const petDto = this.cliente?.pets?.find((p) => p.id == Number.parseInt(petId));
    if (!petDto) return;

    this.mapFromPetDto(petDto);

    if (this.pet.birthdate) {
      this.fechaNacimiento = this.pet.birthdate.format('YYYY-MM-DD');
    }
  }

  createPet(): CreateUpdatePetDto {
    const dto = new CreateUpdatePetDto();
    dto.init({
      id: undefined, name: undefined, birthdate: undefined, speciesId: undefined,
      breedId: undefined, microChip: undefined, color: undefined, pictureUrl: undefined,
      gender: undefined, sourceType: undefined,
      customerIdUnico: this.cliente?.idUnico, customerDocumentNumber: this.cliente?.dni,
    });
    return dto;
  }

  mapFromPetDto(dto: PetDto) {
    this.pet.id = dto.id;
    this.pet.name = dto.name;
    this.pet.birthdate = dto.birthdate;
    this.pet.speciesId = dto.species?.id;
    this.pet.breedId = dto.breed?.id;
    this.pet.microChip = dto.microChip;
    this.pet.color = dto.color;
    this.pet.pictureUrl = dto.pictureUrl;
    this.pet.gender = dto.gender?.id;
    this.pet.sourceType = dto.sourceType;
    this.razaTexto = dto.breed?.name ?? '';
  }

  // ── Especie (botones) ─────────────────────────────────────────────────────
  /** CANINO/FELINO vienen asi del catalogo; en pantalla van lindos. */
  nombreEspecie(s: SpeciesDto): string {
    const n = (s.name ?? '').toUpperCase();
    if (n === 'CANINO') return 'Perro';
    if (n === 'FELINO') return 'Gato';
    return (s.name ?? '').charAt(0).toUpperCase() + (s.name ?? '').slice(1).toLowerCase();
  }

  elegirEspecie(s: SpeciesDto) {
    if (this.pet.speciesId === s.id) return;
    this.pet.speciesId = s.id;
    // Cambio de especie: la raza anterior ya no aplica.
    this.pet.breedId = undefined as any;
    this.razaTexto = '';
    this.reloadBreed(s.id!, false);
  }

  reloadBreed(specieId: number, conservarSeleccion: boolean) {
    this.vetService.getBreeds(specieId).subscribe((razas) => {
      this.razas = razas ?? [];
      // Al recargar por cambio de especie no se conserva; en edicion si.
      if (!conservarSeleccion) return;
    });
  }

  // ── Raza (buscador) ───────────────────────────────────────────────────────
  get razasFiltradas(): BreedDto[] {
    const t = this.razaTexto.trim().toLowerCase();
    if (!t) return this.razas.slice(0, 50);
    return this.razas.filter((r) => (r.name ?? '').toLowerCase().includes(t)).slice(0, 50);
  }

  alEscribirRaza() {
    this.razaListaAbierta = true;
    // Si el texto no coincide exacto con la raza elegida, se deselecciona.
    const exacta = this.razas.find((r) => (r.name ?? '').toLowerCase() === this.razaTexto.trim().toLowerCase());
    this.pet.breedId = (exacta?.id ?? undefined) as any;
  }

  elegirRaza(r: BreedDto) {
    this.pet.breedId = r.id;
    this.razaTexto = r.name ?? '';
    this.razaListaAbierta = false;
  }

  abrirListaRaza() { if (this.pet.speciesId) this.razaListaAbierta = true; }
  cerrarListaRaza() { setTimeout(() => (this.razaListaAbierta = false), 150); }

  // ── Sexo ──────────────────────────────────────────────────────────────────
  elegirSexo(id: number) { this.pet.gender = id; }

  // ── Fecha de nacimiento ───────────────────────────────────────────────────
  usarFechaAproximada(aprox: boolean) { this.fechaAproximada = aprox; }

  get edadCalculada(): string {
    if (this.fechaAproximada || !this.fechaNacimiento) return '';
    const edad = this.appService.calcularEdad(this.fechaNacimiento);
    return edad.años < 100 ? this.appService.calcularEdadString(edad) : '';
  }

  /**
   * La fecha que se guarda. Con fecha exacta, la ingresada. Con fecha aproximada,
   * se estima restando la edad a hoy: el backend solo guarda una fecha, asi que
   * la aproximada se convierte en una fecha estimada.
   */
  private resolverBirthdate(): moment.Moment | undefined {
    if (this.fechaAproximada) {
      if (!this.edadValor || this.edadValor <= 0) return undefined;
      const unidad = this.edadUnidad === 'anios' ? 'years' : 'months';
      return moment().subtract(this.edadValor, unidad).startOf('day');
    }
    return this.fechaNacimiento ? moment(this.fechaNacimiento) : undefined;
  }

  get formValido(): boolean {
    return !!this.pet.name && !!this.pet.speciesId && !!this.pet.gender
        && !!this.pet.breedId && !!this.resolverBirthdate();
  }

  // ── Foto ──────────────────────────────────────────────────────────────────
  // La apertura del selector la maneja el <label for> del template (nativo), no
  // un ViewChild + click(): con el input dentro de un *ngIf el ViewChild static
  // no lo encontraba y el boton no hacia nada.
  cargarImagen(event: Event) {
    const element = event.target as HTMLInputElement;
    const archivo = element && element.files ? element.files[0] : null;
    if (!archivo || !archivo.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = () => {
      this.imgSelectedURLData = reader.result as string;
      this.imgSelectedFile = archivo;
    };
    reader.readAsDataURL(archivo);
  }

  getImage() {
    if (this.imgSelectedURLData) return this.imgSelectedURLData;
    return this.pet.pictureUrl ? this.pet.pictureUrl : this.urlPet;
  }

  get tieneFoto(): boolean {
    return !!this.imgSelectedURLData || !!this.pet.pictureUrl;
  }

  // ── Guardar ───────────────────────────────────────────────────────────────
  async save() {

    if (!this.formValido) return;

    this.pet.birthdate = this.resolverBirthdate();
    this.pet.customerIdUnico = this.cliente?.idUnico;

    if (this.imgSelectedFile) {
      this.isLoading = true;
      try {
        // Se sube directo a la API de Vet (File/Upload). Esta via conserva el
        // binario intacto (a diferencia del lambda, cuyo API Gateway lo corrompia
        // tratandolo como UTF-8). Pendiente: habilitar CORS para micuenta-qa en la
        // API de Vet, o proxear por nginx; hoy el navegador bloquea la respuesta.
        const source$ = this.vetExtendedService.uploadPictureToVet(this.imgSelectedFile);
        const respuesta: any = await firstValueFrom(source$);
        // File/Upload devuelve la URL en result.path; si el interceptor desenvolvio
        // el result, viene en path a secas. Se contemplan ambos.
        this.pet.pictureUrl = respuesta?.result?.path ?? respuesta?.path;
      } catch (error) {
        this.isLoading = false;
        console.error('error al subir la imagen', error);
      }
    }

    this.isLoading = true;
    const guardar$ = this.isEdit ? this.vetService.updatePet(this.pet) : this.vetService.createPet(this.pet);

    guardar$.pipe(finalize(() => (this.isLoading = false))).subscribe(() => {
      this.appService.deleteClienteAppData();
      this.snackBar.open('Los cambios se guardaron correctamente.', '', { duration: 2000 });
      this.router.navigateByUrl(this.isEdit ? '/mis-mascotas' : '/home');
    });
  }
}
